import { z } from 'zod';

/**
 * PROJECT AI CREDITS — the producer-facing vocabulary, one file for the API, the worker and the SPA.
 *
 * `docs/contracts/ai-credits.json` is the source; this is its TypeScript mirror, and
 * `backend/src/__tests__/ai-credits-contract.test.ts` reconciles every list here against it in
 * both directions, so a value added on one side alone fails the build. Laravel owns the money
 * (the ledger, prices, purchases); the workspace only shows it, so nothing here computes an
 * amount: every figure arrives as integer euro CENTS, already rounded for display by the backend
 * (`DisplayRule`, design C.2), and the SPA does no arithmetic on it.
 *
 * NEVER SHOWN TO A PRODUCER, anywhere: provider cost, per-token price, markup, margin, profit, FX,
 * the pricing version or the provider budget (design B.8). The producer DTOs below are STRICT
 * objects, so a mapper that copies one key too many fails its parse instead of shipping it, and
 * `FORBIDDEN_PRODUCER_KEYS` is the list a response scan refuses whatever schema it passed.
 */

/**
 * WHERE THE PLATFORM STANDS ON METERED AI (design C.3), carried on the entitlement.
 *
 * `LEGACY` is exactly today's behaviour: unmetered on paid plans, `AI_NOT_IN_PLAN` on Free.
 * `METERED`: every billable AI operation is estimated, approved, reserved and settled.
 * `SUSPENDED`: launched, then switched off — new paid AI and purchases are refused
 * (`AI_BILLING_UNAVAILABLE`), running operations still settle.
 */
export const AiBilling = z.enum(['LEGACY', 'METERED', 'SUSPENDED']);
export type AiBilling = z.infer<typeof AiBilling>;

/** What a billable AI operation does. A new kind of paid AI work is a new entry, with its version. */
export const AiOperationType = z.enum(['SCRIPT_ANALYSIS', 'SCRIPT_OCR']);
export type AiOperationType = z.infer<typeof AiOperationType>;

/**
 * Each operation type's version and the title a producer reads. The version is part of the
 * quote: a change to how an operation is sized or priced bumps it, so an estimate made under the
 * old rules is never approved under the new ones.
 */
export const AI_OPERATION_TYPE_DEFINITIONS: Readonly<
  Record<AiOperationType, { readonly version: string; readonly title: string }>
> = Object.freeze({
  SCRIPT_ANALYSIS: { version: '1', title: 'AI script analysis' },
  SCRIPT_OCR: { version: '1', title: 'Script reading (scanned PDF)' },
});

/** An operation's lifecycle (design C.5). Terminal: SETTLED, CANCELLED, EXPIRED. */
export const AiOperationStatus = z.enum([
  'ESTIMATED',
  'RESERVED',
  'RUNNING',
  'SETTLEMENT_PENDING',
  'RECONCILIATION_REQUIRED',
  'SETTLED',
  'CANCELLED',
  'EXPIRED',
]);
export type AiOperationStatus = z.infer<typeof AiOperationStatus>;

export const AI_OPERATION_TERMINAL_STATUSES: readonly AiOperationStatus[] = Object.freeze([
  'SETTLED',
  'CANCELLED',
  'EXPIRED',
]);

/** How a finished operation ended. Null on an operation that has not finished. */
export const AiOperationOutcome = z.enum(['completed', 'partial', 'stopped_at_cap', 'failed']);
export type AiOperationOutcome = z.infer<typeof AiOperationOutcome>;

/** A credit purchase's state. Only Stripe's webhook makes one PAID; the return page proves nothing. */
export const AiCreditPurchaseStatus = z.enum([
  'PENDING',
  'PROCESSING',
  'PAID',
  'FAILED',
  'CANCELLED',
  'REFUNDED',
  'PARTIALLY_REFUNDED',
]);
export type AiCreditPurchaseStatus = z.infer<typeof AiCreditPurchaseStatus>;

/** Why a purchase carries the VAT it does (`VatRates::resolve()`, owner decision P11). */
export const AiCreditVatReason = z.enum(['standard', 'reverse_charge', 'outside_bulgaria', 'outside_eu']);
export type AiCreditVatReason = z.infer<typeof AiCreditVatReason>;

/**
 * The history's kinds and the label each is shown under (design B.8). Keyed by the stable code,
 * so a relabel is a copy change and never a data change.
 */
export const AI_CREDIT_TRANSACTION_KIND_LABELS = Object.freeze({
  initial_credits: 'Initial credits',
  credits_purchased: 'Credits purchased',
  ai_operation: 'AI operation',
  credit_refund: 'Credit refund',
  credit_adjustment: 'Credit adjustment',
  bonus_credits: 'Bonus credits',
} as const);

export const AiCreditTransactionKind = z.enum(
  Object.keys(AI_CREDIT_TRANSACTION_KIND_LABELS) as [
    keyof typeof AI_CREDIT_TRANSACTION_KIND_LABELS,
    ...(keyof typeof AI_CREDIT_TRANSACTION_KIND_LABELS)[],
  ],
);
export type AiCreditTransactionKind = z.infer<typeof AiCreditTransactionKind>;

/**
 * Every coded refusal of the credits surface, with the HTTP status it is answered with. A refusal
 * for lack of credits or of a plan is shown as an OFFER (toned ok or warn, never danger, B.7).
 */
export const AI_CREDIT_REFUSAL_HTTP_STATUS = Object.freeze({
  CREDITS_INSUFFICIENT: 422,
  QUOTE_EXPIRED: 422,
  QUOTE_STALE_INPUT: 422,
  PRICING_UNAVAILABLE: 503,
  AI_BILLING_UNAVAILABLE: 409,
  BILLING_APPROVAL_REQUIRED: 409,
  OPERATION_NOT_FOUND: 404,
  OPERATION_STATE_CONFLICT: 409,
  PACKAGE_NOT_AVAILABLE: 422,
  PURCHASE_NOT_ALLOWED: 403,
  VAT_RATE_MISSING: 503,
  PRODUCTION_READ_ONLY: 403,
  PROJECT_ARCHIVED: 409,
} as const);

export type AiCreditRefusalCode = keyof typeof AI_CREDIT_REFUSAL_HTTP_STATUS;

export const AiCreditRefusalCode = z.enum(
  Object.keys(AI_CREDIT_REFUSAL_HTTP_STATUS) as [AiCreditRefusalCode, ...AiCreditRefusalCode[]],
);

/**
 * What a provider request is booked to on the AI Router's ledger (`meta.reference_type`).
 *
 * The router writes a `run_intent` row BEFORE calling upstream for exactly these three (design
 * C.8, F2), so a run tagged with one of them can never be billed without a durable record.
 * `ai_billable_operation` carries the operation id as `meta.reference`; the internal two are the
 * eval tool and ops scripts, on their own OpenRouter keys.
 */
export const RouterReferenceType = z.enum(['ai_billable_operation', 'internal_eval', 'internal_ops']);
export type RouterReferenceType = z.infer<typeof RouterReferenceType>;

/* ------------------------------------------------------------------------- *
 * The producer DTOs (`producer_fields` in the contract). Exact key names;
 * strict, so nothing beyond the allowlist can ride along.
 * ------------------------------------------------------------------------- */

const cents = z.number().int();

/** `GET /v1/projects/:projectId/credits`. */
export const AiCreditWalletDto = z.strictObject({
  projectId: z.string(),
  aiBilling: AiBilling,
  /** What can be spent now: paid and promotional together. */
  availableCents: cents,
  /** Held for operations approved and not yet settled. */
  reservedCents: cents,
  /** Held back while a card dispute on a purchase is open. */
  frozenCents: cents,
  purchasedCents: cents,
  bonusCents: cents,
  consumedCents: cents,
});
export type AiCreditWalletDto = z.infer<typeof AiCreditWalletDto>;

/** One line of the history. `amountCents` is signed: a charge is negative. */
export const AiCreditTransactionDto = z.strictObject({
  id: z.string(),
  occurredAt: z.string(),
  kind: AiCreditTransactionKind,
  label: z.string(),
  /** The operation's title on an AI operation line; null on every other kind. */
  operationTitle: z.string().nullable(),
  amountCents: cents,
  /** The line's display state, as the backend words it; the contract fixes no vocabulary for it. */
  status: z.string(),
  balanceAfterCents: cents,
});
export type AiCreditTransactionDto = z.infer<typeof AiCreditTransactionDto>;

/** `GET …/credits/transactions?cursor=`. `nextCursor` is null on the last page. */
export const AiCreditTransactionsPageDto = z.strictObject({
  items: z.array(AiCreditTransactionDto),
  nextCursor: z.string().nullable(),
});
export type AiCreditTransactionsPageDto = z.infer<typeof AiCreditTransactionsPageDto>;

/** One package of `GET …/credits/packages`. */
export const AiCreditPackageDto = z.strictObject({
  code: z.string(),
  creditsCents: cents,
  /** Excluding VAT, which is added on top at purchase (P11). */
  priceCents: cents,
});
export type AiCreditPackageDto = z.infer<typeof AiCreditPackageDto>;

/** `GET …/credits/packages`: the catalogue, in one envelope (`packages_list`, contract 1.1.0). */
export const AiCreditPackagesListDto = z.strictObject({
  packages: z.array(AiCreditPackageDto),
});
export type AiCreditPackagesListDto = z.infer<typeof AiCreditPackagesListDto>;

/** `POST …/credits/purchases`: what the Payment Element needs, and the VAT it was told. */
export const AiCreditPurchaseStartedDto = z.strictObject({
  purchaseUuid: z.string(),
  status: AiCreditPurchaseStatus,
  clientSecret: z.string(),
  publishableKey: z.string(),
  creditsCents: cents,
  subtotalCents: cents,
  taxCents: cents,
  totalCents: cents,
  vatRateBps: z.number().int(),
  vatReason: AiCreditVatReason,
});
export type AiCreditPurchaseStartedDto = z.infer<typeof AiCreditPurchaseStartedDto>;

/** `GET …/credits/purchases/:purchaseUuid`, polled after the card step. */
export const AiCreditPurchaseStatusDto = z.strictObject({
  purchaseUuid: z.string(),
  status: AiCreditPurchaseStatus,
  creditsCents: cents,
  totalCents: cents,
});
export type AiCreditPurchaseStatusDto = z.infer<typeof AiCreditPurchaseStatusDto>;

/**
 * `POST …/ai-operations/estimate`. `capCents` is the displayed maximum and the most the operation
 * can ever be charged (P6); `remainingAfterMaxCents` is negative when the balance falls short,
 * which is what `sufficient` says in a word.
 */
export const AiOperationEstimateDto = z.strictObject({
  operationId: z.string(),
  operationType: AiOperationType,
  title: z.string(),
  status: AiOperationStatus,
  minCents: cents,
  maxCents: cents,
  capCents: cents,
  availableCents: cents,
  remainingAfterMaxCents: cents,
  expiresAt: z.string(),
  sufficient: z.boolean(),
});
export type AiOperationEstimateDto = z.infer<typeof AiOperationEstimateDto>;

/**
 * An operation after approval. The charged and released figures are NULL until it settles —
 * "not settled yet" is not "charged nothing", and a screen must never draw the first as 0.
 */
export const AiOperationDto = z.strictObject({
  operationId: z.string(),
  operationType: AiOperationType,
  title: z.string(),
  status: AiOperationStatus,
  outcome: AiOperationOutcome.nullable(),
  capCents: cents,
  chargedCents: cents.nullable(),
  releasedCents: cents.nullable(),
  /** The operation that picks up where a stopped-at-cap one ended, once one is estimated. */
  continuationOperationId: z.string().nullable(),
  pipelineRunId: z.string().nullable(),
  /**
   * True when the operation was charged more than nothing and less than one cent (contract 1.1.0):
   * `chargedCents` then reads 0 and the screen says "less than €0.01" rather than "€0.00" (C.2).
   */
  chargeBelowOneCent: z.boolean(),
});
export type AiOperationDto = z.infer<typeof AiOperationDto>;

/**
 * A SCANNED PDF WAITING FOR A PAID READING (`paid_reading`, contract 1.1.0; owner decision P6).
 *
 * Carried as `paidReading: PaidReading | null` on the script payloads the step-1 intake polls —
 * `GET /v1/projects/:projectId/script` and `GET /v1/script-versions/:scriptVersionId/progress` —
 * while the latest parse is paused at "Needs reading": the PDF has no text layer and the
 * production's AI is metered, so OCR waits for an approved `SCRIPT_OCR` estimate. `pageCount`
 * sizes that estimate. Null as soon as the reading is approved, and on every other screenplay.
 */
export const PaidReading = z.strictObject({
  status: z.literal('AWAITING_APPROVAL'),
  pageCount: z.number().int().positive(),
});
export type PaidReading = z.infer<typeof PaidReading>;

/**
 * Every producer DTO under its contract name (`producer_fields`), so the reconciliation can walk
 * them without a hand-kept second list.
 */
export const AI_CREDITS_PRODUCER_DTOS = Object.freeze({
  wallet: AiCreditWalletDto,
  transaction: AiCreditTransactionDto,
  transactions_page: AiCreditTransactionsPageDto,
  package: AiCreditPackageDto,
  packages_list: AiCreditPackagesListDto,
  purchase_started: AiCreditPurchaseStartedDto,
  purchase_status: AiCreditPurchaseStatusDto,
  estimate: AiOperationEstimateDto,
  operation: AiOperationDto,
  paid_reading: PaidReading,
});

/**
 * KEYS NO PRODUCER-FACING PAYLOAD MAY CARRY, at any depth: the provider's cost, the markup and
 * margin, FX, the pricing version, the provider budget and the per-model prices behind it. Both
 * spellings of the cost are listed because a payload copied from Laravel's internal answer
 * arrives camelCased while one copied from the router's arrives snake_cased.
 */
export const FORBIDDEN_PRODUCER_KEYS: readonly string[] = Object.freeze([
  'provider',
  'providerCost',
  'providerCostUsd',
  'costUsd',
  'cost_usd',
  'markup',
  'markupBps',
  'margin',
  'profit',
  'fx',
  'fxRate',
  'pricingVersion',
  'pricingVersionId',
  'budget',
  'providerBudgetUsd',
  'usagePlan',
  'prices',
]);

/**
 * The forbidden keys found anywhere in `value`, as `path.to.key`, or an empty list.
 *
 * The response scan the producer API runs on every answer (design D.4) and its tests assert with.
 * Keys are matched exactly — `providerName` is not `provider` — and values are never read, so a
 * title that happens to say "budget" is not a finding.
 */
export function forbiddenProducerKeysIn(value: unknown, path = ''): string[] {
  if (Array.isArray(value)) {
    return value.flatMap((item, index) => forbiddenProducerKeysIn(item, `${path}[${String(index)}]`));
  }
  if (typeof value !== 'object' || value === null) return [];

  return Object.entries(value as Record<string, unknown>).flatMap(([key, child]) => {
    const here = path === '' ? key : `${path}.${key}`;
    const own = FORBIDDEN_PRODUCER_KEYS.includes(key) ? [here] : [];
    return [...own, ...forbiddenProducerKeysIn(child, here)];
  });
}
